class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector("[data-slider]");
    this.isCarousel = this.hasAttribute("data-carousel-slider");
    this.wrapper = this.isCarousel ? this.closest("[data-carousel]") : this;
    this.sliderItems = this.querySelectorAll("[data-slider-item]");
    this.previousButtons = this.wrapper.querySelectorAll("[data-button-previous]");
    this.nextButtons = this.wrapper.querySelectorAll("[data-button-next]");
    this.trackers = this.wrapper.querySelectorAll("[data-slider-tracker]");
    this.isTabbedCarousel = this.hasAttribute("data-tabbed-slider");
    this.enableSliderLooping = false;

    if (!this.slider) return;

    if (this.isCarousel) this.wrapper = this.closest("[data-carousel]");
    if (this.isTabbedCarousel) this.updateElements();

    this.addEventListeners();
    this.initPages();

    const resizeObserver = new ResizeObserver(() => this.initPages());

    resizeObserver.observe(this.slider);
  }

  /**
   * Adds event listeners for slider scroll and navigation buttons.
   */
  addEventListeners() {
    this.slider.addEventListener("scroll", () => this.update());
    this.previousButtons.forEach(button => button.addEventListener("click", event => this.onButtonClick(event)));
    this.nextButtons.forEach(button => button.addEventListener("click", event => this.onButtonClick(event)));
  }

  /**
   * Initializes pagination by calculating the number of pages and slides per page.
   */
  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);

    if (this.sliderItemsToShow.length < 2) return;

    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

    this.update();
  }

  /**
   * Updates the navigation buttons and trackers for tabbed carousels.
   */
  updateElements() {
    const tabbedCarousel = this.closest("[data-tabbed-carousel]");
    const index = this.dataset.tabIndex;
    this.previousButtons = tabbedCarousel.querySelectorAll(`[data-button-previous][data-tab-index="${index}"]`);
    this.nextButtons = tabbedCarousel.querySelectorAll(`[data-button-next][data-tab-index="${index}"]`);
    this.trackers = tabbedCarousel.querySelectorAll(`[data-slider-tracker][data-tab-index="${index}"]`);
  }

  /**
   * Updates the current page based on the slider's scroll position and updates buttons and trackers.
   */
  update() {
    if (!this.slider) return;

    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    this.updateButtons();
    this.setTrackerTimeout();
  }

  /**
   * Enables or disables navigation buttons based on the current slide position.
   */
  updateButtons() {
    const isFirstSlide = this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0;
    const isLastSlide = this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1]);

    this.previousButtons.forEach(button => isFirstSlide
      ? button.setAttribute("disabled", "disabled")
      : button.removeAttribute("disabled")
    );

    this.nextButtons.forEach(button => isLastSlide
      ? button.setAttribute("disabled", "disabled")
      : button.removeAttribute("disabled")
    );
  }

  /**
   * Handles the click event on navigation buttons to scroll the slider.
   * @param {Event} event
   */
  onButtonClick(event) {
    event.preventDefault();

    const step = event.currentTarget.dataset.step || 1;

    this.slideScrollPosition = event.currentTarget.hasAttribute("data-button-next")
      ? this.slider.scrollLeft + step * this.sliderItemOffset
      : this.slider.scrollLeft - step * this.sliderItemOffset;

    this.slider.scrollTo({ left: this.slideScrollPosition });
  }

  /**
   * Sets a timeout to update the tracker elements after a short delay.
   */
  setTrackerTimeout() {
    clearTimeout(this.runTracker);

    this.runTracker = setTimeout(() => this.updateTrackers(), 25);
  }

  /**
   * Calculates the percentage of slides visible and updates the tracker elements accordingly.
   */
  updateTrackers() {
    let lastSlideVisible = 0;

    this.sliderItemsToShow.forEach((item, index) => {
      if (this.isSlideVisible(item)) lastSlideVisible = index + 1;
    });

    const percentage = (lastSlideVisible / this.sliderItemsToShow.length) * 100;

    this.updateTracker(percentage);
  }

  /**
   * Updates the width of the tracker elements based on the percentage of slides visible.
   * @param {number} percentage
   */
  updateTracker(percentage) {
    this.trackers.forEach(tracker => tracker.style.width = percentage + "%")
  }

  /**
   * Checks if a slide is visible within the slider viewport.
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  isSlideVisible(element) {
    const sliderRect = this.slider.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    // Only count as visible if the entire slide is within the slider viewport
    return (
      elementRect.left >= sliderRect.left &&
      elementRect.right <= sliderRect.right
    );
  }
}

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();

    this.CONTROL_LINKS_TO_SHOW = 5;

    this.sliderFirstItemNode = this.querySelector("[data-slider-item]");
    this.sliderControlWrapper = this.querySelector("[data-slider-counter]");
    this.sliderControlLinksArray = this.sliderControlWrapper?.querySelectorAll("[data-slider-dot]");
    this.sliderControlTrack = this.querySelector("[data-slider-counter-track]");

    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.setSlideVisibility();
    this.addEventListeners();
  }

  /**
   * Adds event listeners for slider controls and scroll events.
   */
  addEventListeners() {
    super.addEventListeners();

    this.sliderControlLinksArray?.forEach(link => link.addEventListener("click", () => this.linkToSlide(link)));
    this.slider.addEventListener("scroll", () => this.setSlideVisibility());

    subscribe(PUB_SUB_EVENTS.slideSelected, event => this.linkToSlide(event, "instant"));
  }

  /**
   * Handles the click event on navigation buttons to scroll the slider with looping behavior.
   * @param {Event} event
   */
  onButtonClick(event) {
    super.onButtonClick(event);

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.slider.scrollTo({ left: this.slideScrollPosition });
  }

  /**
   * Updates the slider controls to reflect the current page.
   */
  update() {
    super.update();

    this.sliderControlButtons = this.querySelectorAll("[data-slider-dot]");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove("slider-dot--active");
      link.removeAttribute("aria-current");
    });

    this.sliderControlButtons[this.currentPage - 1]?.classList.add("slider-dot--active");
    this.sliderControlButtons[this.currentPage - 1]?.setAttribute("aria-current", true);

    if (this.sliderControlTrack) this.updateTrackPosition(this.sliderControlButtons);
  }

  updateTrackPosition(dots) {
    const visible = 5
    const indicatorWidth = dots[1].offsetLeft - dots[0].offsetLeft;
    const total = dots.length
    const maxStart = Math.max(total - visible, 0);
    const activeIndex = this.currentPage - 1;
    const startIndex = Math.min(
      Math.max(activeIndex - Math.floor(visible / 2), 0),
      maxStart
    )

    this.sliderControlTrack.style.transform = `translateX(-${startIndex * indicatorWidth}px)`
  }

  /**
   * Sets the visibility and accessibility attributes for each slide based on the current page.
   */
  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");

      this.setSlideState(item, linkElements, index === this.currentPage - 1);
    });
  }

  /**
   * Sets the state of a slide and its links based on whether it is active.
   * @param {HTMLElement} item
   * @param {NodeList} links
   * @param {boolean} isActive
   */
  setSlideState = (item, links, isActive) => {
    links.forEach(link => link.setAttribute("tabindex", isActive ? "0" : "-1"));
    item.setAttribute("aria-hidden", isActive ? "false" : "true");

    isActive
      ? item.removeAttribute("tabindex")
      : item.setAttribute("tabindex", "-1");
  };

  /**
   * Links to a specific slide based on the provided slide element or event.
   * @param {HTMLElement|Event} slide
   * @param {string} behavior
   */
  linkToSlide(slide, behavior = "smooth") {
    const sliderId = slide.dataset.sliderId;
    const slideIndex = slide.dataset.index;

    if (sliderId && this.id !== sliderId) return;

    this.slider.scrollTo({ left: slideIndex * this.sliderItemOffset, behavior });
  }
}

class Drawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
  }

  /**
   * Called when the element is added to the DOM.
   */
  connectedCallback() {
    this.addListeners();
  }

  /**
   * Adds event listeners to the drawer elements.
   */
  addListeners() {
    this.closeButtons.forEach(button => button.addEventListener("click", () => this.close()));
    this.overlay?.addEventListener("click", () => this.close());
    this.addEventListener("keyup", event => event.code === "Escape" && this.close());

    subscribe(PUB_SUB_EVENTS.closeDrawer, drawer => {
      if (drawer === this) this.close();
    });
  }

  /**
   * Opens the drawer.
   * @param {HTMLElement} triggeredBy
   */
  open(triggeredBy = null) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    document.body.classList.add("body--overlay");

    this.classList.add("drawer--animate", "drawer--active");

    this.addEventListener('transitionend', () => this.setFocus(), { once: true });
  }

  /**
   * Sets focus to the drawer content or empty state.
   */
  setFocus() {
    const isEmpty = this.classList.contains("drawer--is-empty");
    const emptyContent = this.querySelector(".drawer__empty");
    const drawerContent = this.querySelector(".drawer__inner");
    const containerToTrapFocusOn = isEmpty ? emptyContent : drawerContent;

    trapFocus(containerToTrapFocusOn, this.closeButton);
  }

  /**
   * Closes the drawer.
   */
  close() {
    this.classList.remove("drawer--active");

    removeTrapFocus(this.activeElement);

    document.body.classList.remove("body--overlay");

    publish(PUB_SUB_EVENTS.drawerClosed);
  }

  /**
   * Sets the element that triggered the drawer.
   * @param {HTMLElement} element
   */
  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * Returns the drawer overlay element.
   * @returns {HTMLElement}
   */
  get overlay() {
    return this.querySelector("[data-drawer-overlay]");
  }

  /**
   * Returns the drawer close button.
   * @returns {HTMLElement}
   */
  get closeButtons() {
    return this.querySelectorAll('[data-close-button]');
  }
}

class DrawerButton extends Drawer {
  constructor() {
    super();

    if (!this.drawerTag || !this.drawer) return;

    this.addEventListener("click", event => this.handleClick(event));
    this.addEventListener("keydown", event => event.code === "Enter" && this.handleClick(event));
  }

  /**
   * Handles the click event to open or close the drawer.
   */
  handleClick() {
    const action = this.dataset.action ? this.dataset.action : "open";

    action === "open" ? this.drawer.open(this) : this.drawer.close();
  }

  /**
   * Returns the drawer tag from dataset.
   * @returns {string}
   */
  get drawerTag() {
    return this.dataset.drawerTag;
  }

  /**
   * Returns the drawer element by ID.
   * @returns {HTMLElement}
   */
  get drawer() {
    return document.getElementById(this.drawerTag);
  }
}

class Dropdown extends HTMLElement {
  constructor() {
    super();

    this.EXPANDED_CLASS = "dropdown--expanded";
    this.ACTIVE_OPTION_CLASS = "dropdown__option--active";

    this.isExpanded = false;
    this.boundHandleBodyClick = this.handleBodyClick.bind(this);
  }

  /**
   * Called when the element is added to the DOM.
   * Sets up event listeners.
   */
  connectedCallback() {
    this.addListeners();
  }

  /**
   * Adds event listeners to the trigger and options.
   */
  addListeners() {
    this.trigger.addEventListener("keydown", event => this.handleTriggerKeyPress(event));
    this.trigger.addEventListener("click", () => this.handleTriggerClick());
  }

  /**
   * Closes the dropdown if a click occurs outside of it.
   * @param event
   */
  handleBodyClick(event) {
    if (!this.contains(event.target) && this.isExpanded) {
      this.close();
    }
  }

  /**
   * Toggles the dropdown open/closed when the trigger is clicked.
   */
  handleTriggerClick() {
    this.isExpanded ? this.close() : this.open();
  }

  /**
   * Opens the dropdown.
   */
  open() {
    this.trigger.setAttribute('aria-expanded', "true");
    this.trigger.focus();
    this.classList.add(this.EXPANDED_CLASS);
    this.isExpanded = true;

    document.addEventListener("click", this.boundHandleBodyClick);
  }

  /**
   * Closes the dropdown.
   */
  close() {
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.focus();
    this.classList.remove(this.EXPANDED_CLASS);
    this.isExpanded = false;

    document.removeEventListener("click", this.boundHandleBodyClick);
  }

  /**
   * Handles key presses on the select trigger.
   * @param event
   */
  handleTriggerKeyPress(event) {
    event.preventDefault();

    switch (event.key) {
      case "Tab":
        this.close();
        break;

      case "Escape":
      case "Enter":
        this.isExpanded ? this.close() : this.open();
    }
  }

  /**
   * Returns the trigger button element.
   */
  get trigger() {
    return this.querySelector("[data-dropdown-trigger]");
  }

  /**
   * @returns {HTMLElement} The dropdown element
   */
  get dropdown() {
    return this.querySelector("[data-dropdown]");
  }
}

class Modal extends HTMLElement {
  constructor() {
    super();

    this.isLightbox = this.hasAttribute("data-lightbox");
    this.type = this.dataset.modal;
    this.activeElement = null;

    this.listeners();
  }

  /**
   * Adds event listeners for modal triggers and controls.
   */
  listeners() {
    this.triggers.forEach(trigger => this.addClickListener(trigger));
    this.close.addEventListener("click", () => this.closeModal());
    this.overlay.addEventListener("click", () => this.closeModal());
    this.addEventListener("keydown", event => event.key === "Escape" && this.closeModal());
  }

  /**
   * Adds a click event listener to a trigger.
   * @param {HTMLElement} trigger
   */
  addClickListener(trigger) {
    trigger.addEventListener("click", () => {
      const type = trigger.dataset.modalTrigger;

      if (this.type === type) this.openModalEvent(trigger);
    });
  }

  /**
   * Handles opening the modal and publishing events if needed.
   * @param {HTMLElement} trigger
   */
  openModalEvent(trigger) {
    this.openModal(trigger);
    this.activeElement = trigger;

    if (this.isLightbox) {
      publish(PUB_SUB_EVENTS.slideSelected, trigger);
    }
  }

  /**
   * Opens the modal and traps focus.
   */
  openModal() {
    this.classList.add("modal--active");
    this.tabIndex = 0;
    this.setAttribute("aria-hidden", "false");

    trapFocus(this.content, this.close);

    document.body.classList.add('body--overlay');
  }

  /**
   * Closes the modal and removes focus trap.
   */
  closeModal() {
    this.classList.remove("modal--active");

    removeTrapFocus(this.activeElement);

    document.body.classList.remove('body--overlay');
  }

  /**
   * Returns all modal trigger elements.
   * @returns {NodeListOf<Element>}
   */
  get triggers() {
    return document.querySelectorAll("[data-modal-trigger]");
  }

  /**
   * Returns the modal content element.
   * @returns {HTMLElement}
   */
  get content() {
    return this.querySelector("[data-modal-content]");
  }

  /**
   * Returns the modal overlay element.
   * @returns {HTMLElement}
   */
  get overlay() {
    return this.querySelector("[data-modal-overlay]");
  }

  /**
   * Returns the modal close button element.
   * @returns {HTMLElement}
   */
  get close() {
    return this.querySelector("[data-modal-close]");
  }
}

class LocalizationForm extends HTMLElement {
  constructor() {
    super();

    this.DROPDOWN_ACTIVE = "localisation__list--active";

    this.localisationForms = this.querySelectorAll("[data-localisation]");
    this.form = this.querySelector("[data-localisation-form]");
    this.button = this.querySelector("[data-localisation-button]");
    this.dropdown = this.querySelector("[data-localisation-dropdown]");
    this.links = this.querySelectorAll("[data-localisation-link]");
    this.input = this.querySelector("[data-localisation-code-input]");
    this.active = false;
    this.boundHandleBodyClick = this.handleBodyClick.bind(this);

    this.addListeners();
  }

  /**
   * Adds event listeners for keyboard and click events.
   */
  addListeners() {
    this.addEventListener("keyup", event => this.onContainerKeyUp(event));
    this.links.forEach(link => link.addEventListener('click', event => this.countrySelected(event)));
    this.button.addEventListener("click", () => this.toggleSelector());
  }

  /**
   * Handles country selection from the dropdown.
   * @param {Event} event
   */
  countrySelected(event) {
    event.preventDefault();

    this.input.value = event.currentTarget.dataset.value;

    if (this.form) this.form.submit();
  }

  /**
   * Handles keyup events on the container to close the dropdown on Escape key.
   * @param {KeyboardEvent} event
   */
  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    this.toggleSelector();
    this.dropdown.focus();
  }

  /**
   * Toggles the visibility of the localisation selector.
   */
  toggleSelector() {
    this.active = !this.active;

    this.active ? this.openSelector() : this.closeSelector();
  }

  /**
   * Handles body click events to close the dropdown if clicked outside.
   * @param {Event} event
   */
  handleBodyClick(event) {
    if (!this.contains(event.target) && this.active) {
      this.closeSelector();
    }
  }

  /**
   * Opens the localisation selector dropdown.
   */
  openSelector() {
    this.closeLocalisationForms();
    this.setLinksState();

    this.dropdown.classList.add(this.DROPDOWN_ACTIVE);
    this.dropdown.setAttribute("aria-expanded", "true");

    document.addEventListener("click", this.boundHandleBodyClick);

    trapFocus(this.dropdown);
  }

  /**
   * Closes all localisation forms dropdowns.
   */
  closeLocalisationForms() {
    this.localisationForms.forEach(form => form.classList.remove(this.DROPDOWN_ACTIVE));

    this.dropdown.classList.remove(this.DROPDOWN_ACTIVE);
    this.dropdown.setAttribute("aria-expanded", "false");
  }

  /**
   * Closes the localisation selector dropdown.
   */
  closeSelector() {
    this.active = false;
    this.setLinksState(false);

    this.dropdown.classList.remove(this.DROPDOWN_ACTIVE);
    this.dropdown.setAttribute("aria-expanded", "false");

    document.removeEventListener('click', this.boundHandleBodyClick);
  }

  /**
   * Sets the tabindex of the links based on the active state.
   * @param {boolean} active
   */
  setLinksState(active = true) {
    this.links.forEach(link => link.setAttribute("tabindex", active ? "0" : "-1"));
  }
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector("[data-quantity-input]");
    this.buttons = this.querySelectorAll("[data-quantity-button]");

    this.changeEvent = new Event('change', { bubbles: true });

    this.addListeners();
  }

  /**
   * Adds event listeners to the input and buttons.
   */
  addListeners() {
    this.input.addEventListener('change', () => this.onInputChange());
    this.input.addEventListener('keydown', event => this.onInputKeydown(event));
    this.buttons.forEach(button =>
      button.addEventListener('click', event => this.onButtonClick(event))
    );
  }

  /**
   * Prevents Enter from implicitly submitting the surrounding cart form,
   * while still committing the typed value as a change.
   * @param {KeyboardEvent} event
   */
  onInputKeydown(event) {
    if (event.key !== 'Enter') return;

    event.preventDefault();

    this.input.dispatchEvent(this.changeEvent);
  }

  /**
   * Called when the element is added to the DOM.
   * Validates the quantity rules.
   */
  connectedCallback() {
    this.validateQtyRules();
  }

  /**
   * Handles input change event to validate quantity rules.
   */
  onInputChange() {
    this.validateQtyRules();
  }

  /**
   * Handles button click event to increment or decrement the quantity.
   * @param {Event} event
   */
  onButtonClick(event) {
    event.preventDefault();

    const previousValue = this.input.value;

    event.target.name === 'plus'
      ? this.input.stepUp()
      : this.input.stepDown();

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  /**
   * Validates the quantity input against min and max rules.
   * Disables/enables buttons accordingly.
   */
  validateQtyRules() {
    const value = parseInt(this.input.value);

    if (this.input.min) {
      const buttonMinus = this.querySelector("[data-quantity-button][name='minus']");
      const status = value <= parseInt(this.input.min);

      buttonMinus.toggleAttribute('disabled', status);
    }

    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector("[data-quantity-button][name='plus']");

      buttonPlus.toggleAttribute('disabled', value >= max);
    }
  }
}

customElements.define("slider-component", SliderComponent);
customElements.define("slideshow-component", SlideshowComponent);
customElements.define('drawer-component', Drawer);
customElements.define('drawer-button', DrawerButton);
customElements.define("dropdown-wrapper", Dropdown);
customElements.define("modal-component", Modal);
customElements.define('localization-form', LocalizationForm);
customElements.define('quantity-input', QuantityInput);
